'use client';

import { Card, InlineToast, Spinner } from '@clickt/brand';
import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Check } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { ThemedLogo } from '../components/ThemedLogo.tsx';
import { authClient } from '../lib/auth-client.ts';

export function VerifyEmailPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: '/verify-email' }) as { token?: string };
  const token = search.token ?? '';
  const firedRef = useRef(false);

  const mutation = useMutation({
    mutationFn: async (vars: { token: string }) => {
      const { error } = await authClient.verifyEmail({ query: { token: vars.token } });
      if (error) throw error;
    },
    onSuccess: () => {
      // After verification the user is not auto-signed-in (slice-a locked
      // `autoSignInAfterVerification: false`), so we route them to /login
      // rather than /app.
      setTimeout(() => {
        void navigate({ to: '/login' });
      }, 1500);
    },
  });

  useEffect(() => {
    if (firedRef.current) return;
    if (!token) return;
    firedRef.current = true;
    mutation.mutate({ token });
  }, [token, mutation]);

  return (
    <div className="min-h-screen flex flex-col surface-dotted">
      <header className="flex items-center px-6 lg:px-10 py-6">
        <a
          href="https://clickt.link"
          className="flex items-center rounded-sm focus-visible:outline-none focus-visible:shadow-focus-ring"
          aria-label="clickt home"
        >
          <ThemedLogo className="h-8 w-auto" />
        </a>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md space-y-6">
          <Card padding="lg">
            {!token ? (
              <InlineToast
                tone="danger"
                title="Verification link is invalid."
                description="The link is missing its token. Request a new verification email by signing up again."
              />
            ) : mutation.isPending || mutation.isIdle ? (
              <div className="flex flex-col items-center text-center space-y-4">
                <Spinner />
                <h1 className="font-display text-display-3 font-bold text-midnight leading-tight tracking-[-0.01em]">
                  Verifying your email…
                </h1>
              </div>
            ) : mutation.isSuccess ? (
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="grid h-12 w-12 place-items-center rounded-pill bg-electric/10">
                  <Check className="h-6 w-6 text-electric" aria-hidden />
                </div>
                <h1 className="font-display text-display-3 font-bold text-midnight leading-tight tracking-[-0.01em]">
                  Email verified.
                </h1>
                <p className="text-body-md text-cool-gray">Redirecting to sign in…</p>
              </div>
            ) : (
              <InlineToast
                tone="danger"
                title="Couldn't verify email."
                description={
                  (mutation.error as { message?: string } | undefined)?.message ??
                  'The link may have expired. Request a new verification email by signing up again.'
                }
              />
            )}
          </Card>

          <p className="text-center text-[14px] text-cool-gray">
            <Link
              to="/login"
              className="text-midnight font-medium hover:text-electric transition-colors duration-fast"
            >
              Back to sign in
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}
